import api from './Api';
import { ActivityType } from 'src/types/activityType';
import { Activity } from 'src/types/activity';
import { CompositeFilterDescriptor, FilterDescriptor, State, toODataString } from '@progress/kendo-data-query';
import { ODataResponse } from 'src/types/oDataResponse';
import { Attachment, CRMActivityEntity } from 'src/types/CRMActivityEntity';
import { CRMProgram } from 'src/types/crmProgram';
import { CRMContact } from 'src/types/crmContact';
import { UserSimple } from 'src/types/userSimple';
import { State as ODataState } from '@progress/kendo-data-query';
import moment from 'moment';
import { getWithExpiry, setWithExpiry } from 'src/utils/localStorageUtils';
import { LOCAL_STORAGE_KEYS } from 'src/constants';

async function getAllActivityTypes(): Promise<ActivityType[]> {
  return api.get<ActivityType[]>('/crm/activitytypes');
}

async function getActivityList(dataState: State): Promise<ODataResponse<Activity>> {
  return api.get<ODataResponse<Activity>>(`/odata/CRMActivity?${toODataString(dataState, { utcDates: false })}&$count=true`);
}

/**
 * Get all tasks that are due based on the number of days in the settings
 * @param userId The user id to get tasks for. This will get tasks based on createdByUser and AssignedToUser
 * @param notificationDays The number of days to look ahead for tasks
 */
async function getTasksForNotifications(userId: number, notificationDays: number): Promise<Activity[]> {
  let filterList: (CompositeFilterDescriptor | FilterDescriptor)[] = [];

  filterList.push({
    logic: 'or',
    filters: [
      { field: 'createdByCompanyUserId', operator: 'eq', value: userId },
      { field: 'assignedTo', operator: 'eq', value: userId },
    ],
  });

  filterList.push({ field: 'activityTypeId', operator: 'eq', value: 1 });

  const dueDateEnd = moment().add(notificationDays, 'days').toDate();

  filterList.push({ field: 'dueDate', operator: 'lt', value: dueDateEnd });
  filterList.push({ field: 'isComplete', operator: 'eq', value: false });

  const dataState: ODataState = {
    skip: 0,
    take: 50,
    filter: {
      filters: filterList,
      logic: 'and',
    },
  };

  const res: ODataResponse<Activity> = await api.get<ODataResponse<Activity>>(`/odata/CRMActivity?${toODataString(dataState, { utcDates: false })}&$count=true`);

  return res.value;
}

/**
 * Insert or update an activity
 */
async function upsertActivity(activity: CRMActivityEntity): Promise<CRMActivityEntity> {
  return api.post<CRMActivityEntity>('/crm/UpsertActivity', activity);
}

function deleteActivity(activityId: number): Promise<void> {
  if (!activityId || activityId === 0) {
    return Promise.reject('ActivityId is required');
  }

  return api.delete(`/crm/DeleteActivity?activityId=${activityId}`);
}

function saveAttachment(activityId: number, fileType: string, formData: any, customerNumber?: number): Promise<CRMActivityEntity> {
  return api.postFile<CRMActivityEntity>(formData, `/crm/uploaddocument?activityId=${activityId}&customerNumber=${customerNumber ?? 0}&fileType=${fileType}`);
}

function deleteAttachment(attachment: Attachment): Promise<CRMActivityEntity> {
  return api.post<CRMActivityEntity>('/crm/deletedocument', attachment);
}

function downloadAttachment(attachment: Attachment): Promise<any[]> {
  return api.post<any[]>('/crm/downloaddocument', attachment);
}
function getAllCrmPrograms(): Promise<CRMProgram[]> {
  return api.get<CRMProgram[]>('/crm/programs');
}

function getCrmContactsByCustomer(customerNumber: number): Promise<CRMContact[]> {
  return api.get<CRMContact[]>('/crm/contacts?customerNumber=' + customerNumber);
}

async function getCrmUsers(): Promise<UserSimple[]> {
  let users = getWithExpiry<UserSimple[]>(LOCAL_STORAGE_KEYS.AllCrmUsers);

  if (users) {
    return users;
  }

  users = await api.get<UserSimple[]>('/crm/CrmUsers');
  setWithExpiry(LOCAL_STORAGE_KEYS.AllCrmUsers, users, 120);
  return users;
}

/**
 * Get the full entity not just the view. This will include program and contact lists
 */
function getCrmActivity(activityId: number): Promise<CRMActivityEntity> {
  return api.get<CRMActivityEntity>(`/crm/GetCRMActivity?activityId=${activityId}`);
}

/**
 * Get all active users with Application name = 'SFA'
 */
async function getAllSFAUsers(): Promise<UserSimple[]> {
  let sfaUsers = getWithExpiry<UserSimple[]>(LOCAL_STORAGE_KEYS.AllSFAUsers);

  if (sfaUsers) {
    return sfaUsers;
  }

  sfaUsers = await api.get<UserSimple[]>('/User/allSFAUsers');
  setWithExpiry(LOCAL_STORAGE_KEYS.AllSFAUsers, sfaUsers, 120);

  return sfaUsers;
}

export default {
  getAllActivityTypes,
  getActivityList,
  getTasksForNotifications,
  upsertActivity,
  deleteActivity,
  getAllCrmPrograms,
  getCrmActivity,
  getCrmContactsByCustomer,
  getCrmUsers,
  getAllSFAUsers,
  deleteAttachment,
  downloadAttachment,
  saveAttachment,
};
